import { useCallback, useContext, useMemo, useState } from 'react';
import { QueryResult, NetworkStatus, useQuery } from '@apollo/client';
import { GET_TENDERS, GET_EXPIRING_CONTRACTS } from './queries';
import useSelectedTenders from 'src/reactiveVars/SelectedTendersVar';
import { IBidIdentifier } from 'src/models/bids/Bids/types';
import { DndType } from 'src/types/dnd';
import { ContractEndSearchType, JunctionLogic, SearchOnlyStatus } from '@tendium/prom-types';
import { BidItemType } from '@tendium/prom-types/tender';
import { TendersTableItem } from '../TendersViews';
import {
  ITendersDndItem,
  TendersSearchParams,
  isTendersExpirationStatus,
  ApiTendersItem,
  ApiTendersData,
  ApiTendersVars,
  TendersTypes,
  TendersVars,
  UseApiTendersCtxOptions,
  isDateRange,
  isTendersSort,
  DateRange,
  MappedTendersTypes,
  TendersTypesVars,
  TendersSortName,
  TendersSortOrder
} from './types';
import { useCommentsCount } from 'src/models/comments/hooks';
import { CommentParentType } from 'src/models/comments/types';
import { TendersListData } from '.';
import { createApiTendersCtx } from './context';
import {
  isBoolean,
  isString,
  isStrings,
  notUndefined,
  updateQueryParams,
  useQueryParams,
  splitByQuotation,
  isNumbers,
  isNumber
} from 'src/helpers';
import { useLoadAllMps } from 'src/models/matchingProfiles/hooks';
import dayjs from 'src/helpers/dayjs';
import { useParams } from 'react-router';

export function useReadUnreadIdsFromSelectedTenders(): { selectedIsReadIds: string[]; selectedIsUnReadIds: string[] } {
  const [selectedTenders] = useSelectedTenders();
  const selectedIsReadIds = useMemo(
    () => selectedTenders.filter(proc => proc.isRead).map(proc => proc.id),
    [selectedTenders]
  );
  const selectedIsUnReadIds = useMemo(
    () => selectedTenders.filter(proc => !proc.isRead).map(proc => proc.id),
    [selectedTenders]
  );
  return { selectedIsReadIds, selectedIsUnReadIds };
}

export function useOriginsIdsFromSelectedTenders(): string[] {
  const [selectedTenders] = useSelectedTenders();
  return useMemo(() => selectedTenders.filter(tender => !tender.bid).map(tender => tender.id), [selectedTenders]);
}

export function useBidIdsFromSelectedTenders(): IBidIdentifier[] {
  const [selectedTenders] = useSelectedTenders();
  return useMemo(() => selectedTenders.map(tender => tender.bid).filter(notUndefined), [selectedTenders]);
}

export function useOriginsIdsForDND(tender: TendersTableItem): string[] | undefined {
  const [selectedTenders] = useSelectedTenders();
  const originIds = useOriginsIdsFromSelectedTenders();

  return useMemo(() => {
    if (!!selectedTenders.length) {
      return originIds;
    } else if (tender.bid) {
      return;
    } else {
      return [tender.id];
    }
  }, [selectedTenders.length, tender.bid, tender.id, originIds]);
}

export function useBidsIdsForDND(tender: TendersTableItem): IBidIdentifier[] | undefined {
  const [selectedTenders] = useSelectedTenders();
  const bidIds = useBidIdsFromSelectedTenders();

  return useMemo(() => {
    if (!!selectedTenders.length) {
      return bidIds;
    } else if (tender.bid) {
      return [tender.bid];
    } else {
      return;
    }
  }, [bidIds, selectedTenders.length, tender.bid]);
}

export function useDNDItem(tender: TendersTableItem): ITendersDndItem {
  const originIds = useOriginsIdsForDND(tender);
  const bidIds = useBidsIdsForDND(tender);

  return {
    type: DndType.TendersItem,
    dndItem: tender,
    bidIds,
    originIds,
    originType: BidItemType.Procurement
  };
}

export type UseApiTenders<TVars> = [
  Pick<QueryResult<ApiTendersData, ApiTendersVars>, 'fetchMore' | 'loading' | 'error' | 'refetch'> & {
    data?: TendersListData<ApiTendersItem>;
    fetchingMore: boolean;
    vars: TVars;
    initVars: TVars;
    apiVars: QueryResult<ApiTendersData, ApiTendersVars>['variables'];
    eventSource: TendersTypes;
  },
  (updVars: Partial<TVars>) => void
];

export function useApiTenders<TVars = TendersVars>(): UseApiTenders<TVars> {
  const context = useContext<UseApiTenders<TVars> | null>(createApiTendersCtx());
  if (!context) {
    throw new Error('useApiTenders must be used under ApiTendersProvider');
  }
  return context;
}

export function useApiTendersCtx<T extends keyof MappedTendersTypes>(type: T): UseApiTenders<MappedTendersTypes[T]> {
  const [fetchComments, { data: commentsData }] = useCommentsCount();
  const options = useTendersVarsConfig(type);
  const { query, initVars } = options;
  const qpVars = useTendersQpVars(type);
  const updateVars = useCallback(
    (vars: Partial<MappedTendersTypes[T]>) => {
      const changedKeys = Object.keys(vars);
      if (changedKeys.some(key => qpMapper[type].some(field => field === key))) {
        updateQueryParams(vars);
      }
    },
    [type]
  );
  const [_vars, _updateVars] = useState<MappedTendersTypes[T]>(() => ({ ...initVars, ...qpVars }));
  const page = initVars.offset ? initVars.offset + 1 : 1;
  const pageSize = initVars.amount ? initVars.amount : 0;

  const _mappedVars: TendersVars = useMemo(() => ({ ..._vars }), [_vars]);
  const variables: ApiTendersVars = useMemo(
    () => ({
      advanced: {
        assignedTo: _mappedVars?.assignedTo?.length ? _mappedVars?.assignedTo : undefined,
        awarded: _mappedVars?.awarded,
        buyerOrgIds: _mappedVars?.buyerOrgIds?.length ? _mappedVars?.buyerOrgIds : undefined,
        buyerSearchStrings: !_mappedVars?.buyerSearchStrings?.length ? undefined : _mappedVars?.buyerSearchStrings,
        contractEnd:
          _mappedVars?.contractEnd?.length === 2
            ? {
                start: new Date(_mappedVars?.contractEnd[0].valueOf()).setUTCHours(0, 0, 0, 0),
                end: new Date(_mappedVars?.contractEnd[1].valueOf()).setUTCHours(23, 59, 59, 0),
                searchMode: _mappedVars?.contractEndSearchMode ?? ContractEndSearchType.WithExtension
              }
            : undefined,
        contractValueRange:
          _mappedVars?.contractValueRange?.min ||
          _mappedVars?.contractValueRange?.max ||
          _mappedVars?.contractValueRange?.currency
            ? {
                min: isNumber(_mappedVars?.contractValueRange.min)
                  ? Number(_mappedVars.contractValueRange.min)
                  : undefined,
                max: isNumber(_mappedVars?.contractValueRange.max)
                  ? Number(_mappedVars.contractValueRange.max)
                  : undefined,
                currency: _mappedVars.contractValueRange.currency
              }
            : undefined,
        cpvCodesExact: _mappedVars?.cpvCodesExact?.length ? _mappedVars?.cpvCodesExact : undefined,
        keywordArgs: _mappedVars?.keywordArgs?.length ? _mappedVars?.keywordArgs : undefined, //NewKeyword[];
        name: _mappedVars?.name,
        nutsCodesExact: _mappedVars?.nutsCodesExact?.length ? _mappedVars?.nutsCodesExact : undefined,
        onlyFilledData: _mappedVars?.onlyFilledData,
        onlyStatus: _mappedVars.onlyStatus,
        procuringAgency: _mappedVars?.agency,
        procuringAgencyTypes: _mappedVars?.procuringAgencyTypes?.length ? _mappedVars?.procuringAgencyTypes : undefined,
        publicationDate: _mappedVars?.published
          ? {
              start: _mappedVars?.published[0],
              end: _mappedVars?.published[1]
            }
          : undefined,
        search: !!_mappedVars?.search ? splitByQuotation(_mappedVars?.search) : undefined,
        submissionDeadline: _mappedVars?.deadline
          ? {
              start: _mappedVars?.deadline[0],
              end: _mappedVars?.deadline[1]
            }
          : undefined,
        supplierOrgIds: _mappedVars?.supplierOrgIds?.length ? _mappedVars?.supplierOrgIds : undefined,
        supplierSearchLogic: _mappedVars?.supplierSearchLogic,
        supplierSearchStrings: _mappedVars?.supplierSearchStrings?.length
          ? _mappedVars?.supplierSearchStrings
          : undefined,
        contractValueIncludeMissingValue: _mappedVars?.contractValueIncludeMissingValue,
        ...(!_mappedVars?.matchingProfileId && {
          cpvCodes: _mappedVars?.cpvCodes?.length ? _mappedVars?.cpvCodes : undefined,
          nutsCodes: _mappedVars?.nutsCodes?.length ? _mappedVars?.nutsCodes : undefined,
          buyerOrgId: _mappedVars?.buyerOrgId
        })
      },
      sortOnField: _mappedVars?.sort,
      amount: pageSize,
      offset: (page - 1) * pageSize,
      matchingProfileId: qpVars.matchingProfileId,
      isUnhandled: _mappedVars.isUnhandled,
      isStarred: _mappedVars?.isStarred
    }),
    [
      _mappedVars?.assignedTo,
      _mappedVars?.awarded,
      _mappedVars?.buyerOrgIds,
      _mappedVars?.buyerSearchStrings,
      _mappedVars?.contractEnd,
      _mappedVars?.contractEndSearchMode,
      _mappedVars?.contractValueRange?.min,
      _mappedVars?.contractValueRange?.max,
      _mappedVars?.contractValueRange?.currency,
      _mappedVars?.cpvCodesExact,
      _mappedVars?.keywordArgs,
      _mappedVars?.name,
      _mappedVars?.nutsCodesExact,
      _mappedVars?.onlyFilledData,
      _mappedVars.onlyStatus,
      _mappedVars?.agency,
      _mappedVars?.procuringAgencyTypes,
      _mappedVars?.published,
      _mappedVars?.search,
      _mappedVars?.deadline,
      _mappedVars?.supplierOrgIds,
      _mappedVars?.supplierSearchLogic,
      _mappedVars?.supplierSearchStrings,
      _mappedVars?.contractValueIncludeMissingValue,
      _mappedVars?.matchingProfileId,
      _mappedVars?.cpvCodes,
      _mappedVars?.nutsCodes,
      _mappedVars?.buyerOrgId,
      _mappedVars?.sort,
      _mappedVars.isUnhandled,
      _mappedVars?.isStarred,
      pageSize,
      page,
      qpVars.matchingProfileId
    ]
  );

  const queryData = useQuery<ApiTendersData, ApiTendersVars>(query ?? GET_TENDERS, {
    variables,
    notifyOnNetworkStatusChange: true,
    skip: !variables,
    onCompleted: async ({ getTenders }) => {
      const ids = getTenders.procurementsWithScore.map(item => item.procurement.id);
      if (!!ids.length) {
        await fetchComments({ variables: { ids, parentType: CommentParentType.Procurement } });
      }
    }
  });

  const updateVarsFn = useCallback(
    (_vars: Partial<MappedTendersTypes[T]>) => {
      const _qpVars = Object.fromEntries(
        Object.entries(_vars).map(([key, value]) => [
          key,
          initVars[key as keyof Partial<MappedTendersTypes[T]>] === value ? undefined : value
        ])
      ) as Partial<MappedTendersTypes[T]>;

      updateVars?.(_qpVars);
      _updateVars(currentVars => ({ ...currentVars, ..._vars }));
    },
    [initVars, updateVars]
  );

  return useMemo(
    () => [
      {
        eventSource: type,
        vars: _vars,
        initVars,
        apiVars: queryData.variables,
        data: queryData.data?.getTenders
          ? new TendersListData(queryData.data.getTenders, commentsData ?? [])
          : undefined,
        fetchMore: queryData.fetchMore,
        fetchingMore: queryData.networkStatus === NetworkStatus.fetchMore,
        refetch: queryData.refetch,
        loading:
          (queryData.loading ||
            queryData.data === undefined ||
            queryData.networkStatus === NetworkStatus.refetch ||
            queryData.networkStatus === NetworkStatus.setVariables) &&
          queryData.networkStatus !== NetworkStatus.fetchMore
      },
      updateVarsFn
    ],
    [
      type,
      _vars,
      initVars,
      queryData.variables,
      queryData.data,
      queryData.fetchMore,
      queryData.networkStatus,
      queryData.refetch,
      queryData.loading,
      commentsData,
      updateVarsFn
    ]
  );
}

export function useTendersQpVars(type: keyof MappedTendersTypes): TendersVars {
  const qp = useQueryParams();
  const deadlineSearchParams = qp[TendersSearchParams.deadline];
  const publishedSearchParams = qp[TendersSearchParams.published];
  const assignedSearchParams = qp[TendersSearchParams.assignedTo];
  const cpvSearchParams = qp[TendersSearchParams.cpvCodes];
  const nutsSearchParams = qp[TendersSearchParams.nutsCodes];
  const buyerOrgIdsParams = qp[TendersSearchParams.buyerOrgIds];
  const buyerOrgStringsParams = qp[TendersSearchParams.buyerSearchStrings];
  const all = useMemo(
    () => ({
      matchingProfileId: isString(qp[TendersSearchParams.matchingProfileId])
        ? qp[TendersSearchParams.matchingProfileId]
        : undefined,
      sort: isTendersSort(qp[TendersSearchParams.sort]) ? qp[TendersSearchParams.sort] : undefined,
      isUnhandled: isBoolean(qp[TendersSearchParams.isUnhandled]) ? qp[TendersSearchParams.isUnhandled] : undefined,
      search: isString(qp[TendersSearchParams.search]) ? qp[TendersSearchParams.search] : undefined,
      name: isString(qp[TendersSearchParams.name]) ? qp[TendersSearchParams.name] : undefined,
      buyerOrgIds:
        isStrings(buyerOrgIdsParams) || isNumbers(buyerOrgIdsParams)
          ? buyerOrgIdsParams.map(id => id.toString())
          : undefined,
      buyerOrgStringsParams: isStrings(buyerOrgStringsParams) ? buyerOrgStringsParams : undefined,
      awarded: isBoolean(qp[TendersSearchParams.awarded]) ? qp[TendersSearchParams.awarded] : undefined,
      onlyStatus: isTendersExpirationStatus(qp[TendersSearchParams.onlyStatus])
        ? qp[TendersSearchParams.onlyStatus]
        : undefined,
      cpvCodes: isStrings(cpvSearchParams) ? cpvSearchParams : undefined,
      nutsCodes: isStrings(nutsSearchParams) ? nutsSearchParams : undefined,
      deadline: isDateRange(deadlineSearchParams) ? deadlineSearchParams : undefined,
      published: isDateRange(publishedSearchParams) ? publishedSearchParams : undefined,
      assignedTo: isStrings(assignedSearchParams) ? assignedSearchParams : undefined,
      profileId: isString(qp[TendersSearchParams.profileId]) ? qp[TendersSearchParams.profileId] : undefined
    }),
    [
      assignedSearchParams,
      buyerOrgIdsParams,
      buyerOrgStringsParams,
      cpvSearchParams,
      deadlineSearchParams,
      nutsSearchParams,
      publishedSearchParams,
      qp
    ]
  );

  return useMemo(
    () =>
      Object.fromEntries(
        Object.entries(all).filter(([key, value]) => qpMapper[type].some(field => field === key) && !!value)
      ),
    [all, type]
  );
}

const qpMapper: TendersTypesVars = {
  All: [
    'assignedTo',
    'awarded',
    'buyerOrgIds',
    'buyerSearchStrings',
    'cpvCodes',
    'deadline',
    'nutsCodes',
    'onlyStatus',
    'published',
    'search',
    'sort'
  ],
  Starred: ['awarded', 'onlyStatus', 'search', 'sort'],
  Mp: ['awarded', 'isUnhandled', 'matchingProfileId', 'onlyStatus', 'search', 'sort'],
  BuyerExpired: [],
  BuyerRecent: [],
  ExpContract: ['profileId']
};

const INIT_TENDERS_VARS: TendersVars = {
  amount: 30,
  offset: 0,
  onlyStatus: SearchOnlyStatus.Recent,
  sort: {
    key: TendersSortName.published,
    sort: TendersSortOrder.DESCENDING
  }
};
export function useTendersVarsConfig<T extends keyof MappedTendersTypes>(
  type: T
): UseApiTendersCtxOptions<MappedTendersTypes[T]> {
  const { data: mpsData } = useLoadAllMps();
  const firstMp = useMemo(() => (mpsData?.length ? mpsData[0].id : undefined), [mpsData]);
  const { orgId } = useParams<{ orgId: string }>();
  const defaultContractEnd = useMemo(
    () => [dayjs().add(6, 'month').valueOf(), dayjs().add(18, 'month').valueOf()] as DateRange,
    []
  );

  return useMemo(() => {
    switch (type) {
      case 'All': {
        return { initVars: INIT_TENDERS_VARS };
      }
      case 'Starred': {
        return { initVars: { ...INIT_TENDERS_VARS, isStarred: true } };
      }
      case 'Mp': {
        return { initVars: { ...INIT_TENDERS_VARS, matchingProfileId: firstMp, isUnhandled: true } };
      }
      case 'BuyerExpired': {
        return {
          initVars: {
            ...INIT_TENDERS_VARS,
            amount: 5,
            onlyStatus: SearchOnlyStatus.Expired,
            buyerOrgId: orgId
          }
        };
      }
      case 'BuyerRecent': {
        return {
          initVars: {
            ...INIT_TENDERS_VARS,
            amount: 5,
            onlyStatus: SearchOnlyStatus.Recent,
            buyerOrgId: orgId
          }
        };
      }
      case 'ExpContract': {
        return {
          initVars: {
            amount: 30,
            offset: 0,
            sort: {
              key: TendersSortName.expiration,
              sort: TendersSortOrder.ASCENDING
            },
            contractEndSearchMode: ContractEndSearchType.LastPossibleEndDate,
            supplierSearchLogic: JunctionLogic.OR,
            contractEnd: defaultContractEnd
          },
          query: GET_EXPIRING_CONTRACTS
        };
      }

      default: {
        return { initVars: INIT_TENDERS_VARS };
      }
    }
  }, [defaultContractEnd, firstMp, orgId, type]);
}
